import { useCallback, useEffect, useMemo, useState } from "react";

import I18nContext from "./context";
import translations, { DEFAULT_LANG, DIR_BY_LANG, LANGS } from "./translations";

const STORAGE_KEY = "codeil.lang";

// First-visit language. Hebrew is the product default (Israeli audience), so it
// wins unconditionally; only a previously saved choice overrides it. The
// browser is still scanned — but exclusively to offer English to visitors who
// have NO Hebrew anywhere in their language list, never to flip a Hebrew user.
const detectInitialLang = () => {
  if (typeof window === "undefined") {
    return DEFAULT_LANG;
  }

  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    if (saved && LANGS.includes(saved)) {
      return saved;
    }
  } catch {
    // localStorage unavailable (private mode) — fall through to detection.
  }

  // Hebrew is the unconditional default for a first visit; visitors switch with
  // the language toggle and their choice is remembered from then on.
  return DEFAULT_LANG;
};

// Resolve a dotted key ("hero.title") against a language tree.
const resolvePath = (tree, path) => {
  return path.split(".").reduce((node, part) => {
    if (node && typeof node === "object" && part in node) {
      return node[part];
    }
    return undefined;
  }, tree);
};

const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectInitialLang);
  const dir = DIR_BY_LANG[lang] || "rtl";

  // Keep <html lang/dir> in sync so native RTL/LTR, fonts and a11y follow.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("lang", lang);
    root.setAttribute("dir", dir);
  }, [lang, dir]);

  const setLang = useCallback((next) => {
    if (!LANGS.includes(next)) {
      return;
    }
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Ignore persistence failures.
    }
  }, []);

  const toggle = useCallback(() => {
    setLang(lang === "he" ? "en" : "he");
  }, [lang, setLang]);

  // t(key) returns strings, arrays or objects from the dictionary. Missing keys
  // fall back to the other language, then to the raw key (never a blank UI).
  const t = useCallback(
    (key) => {
      const primary = resolvePath(translations[lang], key);
      if (primary !== undefined) {
        return primary;
      }
      const fallbackLang = lang === "he" ? "en" : "he";
      const fallback = resolvePath(translations[fallbackLang], key);
      return fallback !== undefined ? fallback : key;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, dir, setLang, toggle, t }), [lang, dir, setLang, toggle, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export default I18nProvider;
